import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import Select from "react-select";
import { toast } from "react-toastify";
import { ImCancelCircle } from "react-icons/im";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import api from "../../config/URL";
import fetchOverAllStudentCompletionForm from "../List/StudentAllCompletedList";
import fetchAllStudentListByCenter from "../List/StudentListByCenter";

// Validation schema
const validationSchema = Yup.object({
  parent: Yup.string().required("Parent is required"),
  student: Yup.string().required("Student is required"),
  course: Yup.string().required("Course is required"),
  packageId: Yup.string().required("Package is required"),
  invoiceDate: Yup.string().required("Invoice Date is required"),
  dueDate: Yup.string().required("Due Date is required"),
  invoicePeriodFrom: Yup.string().required("Invoice Period From is required"),
  invoicePeriodTo: Yup.string().required("Invoice Period To is required"),
  invoiceItems: Yup.array().of(
    Yup.object({
      item: Yup.string().required("Item is required"),
      itemAmount: Yup.number()
        .required("Item Amount is required")
        .typeError("Item Amount must be a number"),
      taxType: Yup.string().required("Tax Type is required"),
      gstAmount: Yup.number()
        .required("GST Amount is required")
        .typeError("GST Amount must be a number"),
      totalAmount: Yup.number()
        .required("Total Amount is required")
        .typeError("Total Amount must be a number"),
    })
  ),
});

function InvoiceAdd({ selectedCenter }) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const studentID = searchParams.get("studentID");
  const navigate = useNavigate();
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentOverallData, setStudentOverallData] = useState({});
  const [centerPackageOptions, setCenterPackageOptions] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [packageOptions, setPackageOptions] = useState([]);
  const [schedulesData, setSchedulesData] = useState([]);
  const [taxData, setTaxData] = useState([]);
  const [loadIndicator, setLoadIndicator] = useState(false);
  const userName = localStorage.getItem("userName") || "Unknown User";

  const formik = useFormik({
    initialValues: {
      center: selectedCenter || "",
      parent: "",
      student: studentID || "",
      course: "",
      schedule: "",
      noOfLessons: "",
      remark: "",
      invoiceDate: new Date().toISOString().split("T")[0],
      dueDate: "",
      packageId: "",
      invoicePeriodFrom: "",
      invoicePeriodTo: "",
      receiptAmount: "",
      creditAdviceOffset: 0,
      gst: 0,
      totalAmount: 0,
      invoiceStatus: "PENDING",
      createdBy: userName,
      invoiceItems: [
        {
          item: "",
          itemAmount: "",
          taxType: "",
          gstAmount: "",
          totalAmount: "",
        },
      ],
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoadIndicator(true);
      try {
        const payload = {
          generateInvoice: {
            centerId: selectedCenter,
            parent: values.parent,
            studentId: values.student,
            courseId: values.course,
            schedule: values.schedule,
            invoiceDate: values.invoiceDate,
            dueDate: values.dueDate,
            packageId: values.packageId,
            noOfLessons: values.noOfLessons,
            invoicePeriodFrom: values.invoicePeriodFrom,
            invoicePeriodTo: values.invoicePeriodTo,
            gst: parseFloat(values.gst || 0),
            creditAdviceOffset: parseFloat(values.creditAdviceOffset || 0),
            totalAmount: parseFloat(values.totalAmount || 0),
            remark: values.remark,
            invoiceStatus: "PENDING",
            createdBy: userName,
            receiptAmount: values.receiptAmount,
          },
          invoiceItemsList: values.invoiceItems.map((item) => ({
            item: item.item,
            itemAmount: parseFloat(item.itemAmount || 0),
            taxType: item.taxType,
            gstAmount: parseFloat(item.gstAmount || 0),
            totalAmount: parseFloat(item.totalAmount || 0),
            createdBy: userName,
          })),
        };

        const response = await api.post("/generateInvoice", payload, {
          headers: { "Content-Type": "application/json" },
        });

        if (response.status === 201) {
          toast.success("Invoice Generated successfully");
          navigate(`/invoice/view/${response.data.id}`);
        } else {
          toast.error(response.data.message || "Failed to generate invoice");
        }
      } catch (error) {
        if (error.response?.status === 409) {
          toast.warning(error.response?.data?.message || "Conflict occurred");
        } else {
          toast.error(error.response?.data?.message || "An error occurred");
        }
      } finally {
        setLoadIndicator(false);
      }
    },
    validateOnChange: false,
    validateOnBlur: true,
  });

  // Fetch students
  const fetchStudent = async () => {
    try {
      let student = [];
      const numericCenterId = Number(selectedCenter);
      if (numericCenterId === 0) {
        student = await fetchOverAllStudentCompletionForm();
      } else {
        student = await fetchAllStudentListByCenter(numericCenterId);
      }

      if (!Array.isArray(student)) {
        throw new Error("API did not return an array");
      }

      const formattedStudent = student.map((std) => ({
        value: std.id,
        label: std.studentNames,
      }));

      setStudentOptions(formattedStudent);

      if (studentID) {
        const defaultStudent = formattedStudent.find(
          (option) => option.value === Number(studentID)
        );
        if (defaultStudent) {
          formik.setFieldValue("student", defaultStudent.value);
          handleStudentChange(defaultStudent.value);
        } else {
          toast.warning("Selected student not found in the available options");
        }
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error(error.message || "Failed to fetch students");
    }
  };

  // Fetch tax settings
  const fetchTaxData = async () => {
    try {
      const response = await api.get("/getAllTaxSetting");
      setTaxData(response.data);
    } catch (error) {
      toast.error("Error fetching tax data");
    }
  };

  // Fetch packages by center ID
  const fetchPackagesByCenter = async () => {
    try {
      const response = await api.get(
        `/getCenterPackageIdsAndNamesByCenterId/${selectedCenter}`
      );
      const formattedPackages = response.data.map((pkg) => ({
        value: pkg.id,
        label: pkg.packageNames,
        noOfLessons: pkg.noOfLesson,
      }));
      setCenterPackageOptions(formattedPackages);
      setPackageOptions(formattedPackages); // Set default package options
    } catch (error) {
      toast.error("Error fetching center packages");
      console.error(error);
    }
  };

  // Reset form fields
  const resetFormFields = () => {
    formik.setValues({
      ...formik.initialValues,
      center: selectedCenter || "",
      invoiceDate: new Date().toISOString().split("T")[0],
      dueDate: new Date(new Date().setMonth(new Date().getMonth() + 1))
        .toISOString()
        .split("T")[0],
      createdBy: userName,
    });
    setCourseOptions([]);
    setPackageOptions(centerPackageOptions); // Reset to center packages
    setSchedulesData([]);
  };

  // Handle student change
  const handleStudentChange = async (studentId) => {
    if (!studentId) {
      resetFormFields();
      return;
    }

    try {
      const response = await api.get(`/getInvoiceInfoByStudentId/${studentId}`);
      const data = response.data;
      setStudentOverallData(data);

      const uniqueCourses = Array.from(
        new Map(
          data.courses.map((course) => [
            `${course.courseId}-${course.courseName}`,
            course,
          ])
        ).values()
      );

      const courses = uniqueCourses.map((course) => ({
        value: course.courseId,
        label: course.courseName,
      }));
      setCourseOptions(courses);

      const schedules = uniqueCourses
        .map((course) => course.classCode)
        .filter((code, index, self) => self.indexOf(code) === index);
      setSchedulesData(schedules);

      formik.setFieldValue("parent", data.parentName || "");
      formik.setFieldValue("remark", data.remarks || "");
      formik.setFieldValue(
        "creditAdviceOffset",
        parseFloat(data.creditAdviseOffset || 0)
      );

      if (uniqueCourses.length > 0) {
        const firstCourse = uniqueCourses[0];
        const courseExists = courses.find(
          (c) => c.value === firstCourse.courseId
        );
        const packageExists = centerPackageOptions.find(
          (p) => p.value === firstCourse.packageId
        );

        formik.setFieldValue(
          "course",
          courseExists ? firstCourse.courseId : courses[0]?.value || ""
        );
        formik.setFieldValue(
          "packageId",
          packageExists
            ? firstCourse.packageId
            : centerPackageOptions[0]?.value || ""
        );
        formik.setFieldValue("noOfLessons", firstCourse.noOfLessons || "");
        formik.setFieldValue(
          "invoicePeriodFrom",
          firstCourse.invoicePeriodFrom || ""
        );
        formik.setFieldValue(
          "invoicePeriodTo",
          firstCourse.invoicePeriodTo || ""
        );
        formik.setFieldValue("schedule", firstCourse.classCode || "");
        formik.setFieldValue(
          "dueDate",
          firstCourse.invoicePeriodTo ||
            new Date(new Date().setMonth(new Date().getMonth() + 1))
              .toISOString()
              .split("T")[0]
        );

        // Set package options based on course
        const coursePackages = data.courses
          .filter((x) => x.courseId === firstCourse.courseId)
          .map((course) => ({
            value: course.packageId,
            label: course.packageName,
            noOfLessons: course.noOfLessons,
          }))
          .filter(
            (p, index, self) =>
              self.findIndex((x) => x.value === p.value) === index
          );
        setPackageOptions(
          coursePackages.length > 0 ? coursePackages : centerPackageOptions
        );

        // Initialize invoice items
        const invoiceItems = [];
        const hasRegistrationFee =
          parseFloat(firstCourse.registerFees || 0) > 0;
        const hasDepositFee = parseFloat(firstCourse.depositFee || 0) > 0;

        if (hasRegistrationFee) {
          invoiceItems.push({
            item: "Registration Fee",
            itemAmount: "0",
            taxType: firstCourse.registerTaxId
              ? firstCourse.registerTaxId.toString()
              : "",
            gstAmount: "0",
            totalAmount: (firstCourse.registerFees || 0).toString(),
          });
        }

        invoiceItems.push({
          item: firstCourse.courseFeeName || "Course Fee",
          itemAmount: "0",
          taxType: firstCourse.courseTaxId
            ? firstCourse.courseTaxId.toString()
            : "",
          gstAmount: "0",
          totalAmount: (firstCourse.courseFee || 0).toString(),
        });

        if (hasDepositFee) {
          invoiceItems.push({
            item: "Deposit Fee",
            itemAmount: "0",
            taxType: firstCourse.depositTaxId
              ? firstCourse.depositTaxId.toString()
              : "",
            gstAmount: "0",
            totalAmount: (firstCourse.depositFee || 0).toString(),
          });
        }

        invoiceItems.forEach((item, index) => {
          if (item.taxType && parseFloat(item.totalAmount)) {
            const tax = taxData.find((t) => t.id === parseInt(item.taxType));
            const totalAmount = parseFloat(item.totalAmount || 0);
            if (tax && totalAmount !== 0) {
              const itemAmount = Math.round(totalAmount / (1 + tax.rate / 100));
              const gstAmount = Math.round(totalAmount - itemAmount);
              invoiceItems[index].itemAmount = itemAmount.toString();
              invoiceItems[index].gstAmount = gstAmount.toString();
            } else {
              invoiceItems[index].itemAmount = totalAmount.toString();
              invoiceItems[index].gstAmount = "0";
            }
          } else {
            invoiceItems[index].itemAmount = item.totalAmount || "0";
            invoiceItems[index].gstAmount = "0";
          }
        });

        formik.setFieldValue("invoiceItems", invoiceItems);

        const totalGST = invoiceItems.reduce(
          (sum, item) => sum + parseFloat(item.gstAmount || 0),
          0
        );
        const totalAmountSum = invoiceItems.reduce(
          (sum, item) => sum + parseFloat(item.totalAmount || 0),
          0
        );
        formik.setFieldValue("gst", totalGST.toString());
        formik.setFieldValue(
          "totalAmount",
          (totalAmountSum - parseFloat(data.creditAdviseOffset || 0)).toString()
        );
      } else {
        resetFormFields();
      }
    } catch (error) {
      toast.error("Error fetching student invoice info");
      console.error(error);
      resetFormFields();
    }
  };

  // Handle course change
  const handleCourseChange = async (courseId) => {
    if (!courseId) {
      resetFormFields();
      return;
    }

    const selectedCourse = studentOverallData.courses.find(
      (course) => course.courseId === courseId
    );
    if (!selectedCourse) {
      resetFormFields();
      return;
    }

    const coursePackages = studentOverallData.courses
      .filter((course) => course.courseId === courseId)
      .map((course) => ({
        value: course.packageId,
        label: course.packageName,
        noOfLessons: course.noOfLessons,
      }))
      .filter(
        (pkg, index, self) =>
          self.findIndex((p) => p.value === pkg.value) === index
      );
    setPackageOptions(
      coursePackages.length > 0 ? coursePackages : centerPackageOptions
    );

    try {
      const response = await api.get(
        `/getInvoiceInfo?studentId=${formik.values.student}&courseId=${courseId}`
      );
      const data = response.data;

      formik.setFieldValue("noOfLessons", data.noOfLessons || "");
      formik.setFieldValue("invoicePeriodFrom", data.invoicePeriodFrom || "");
      formik.setFieldValue("invoicePeriodTo", data.invoicePeriodTo || "");
      formik.setFieldValue("schedule", data.classCode || "");
      formik.setFieldValue(
        "packageId",
        data.packageId ||
          coursePackages[0]?.value ||
          centerPackageOptions[0]?.value ||
          ""
      );
      formik.setFieldValue(
        "dueDate",
        data.invoicePeriodTo ||
          new Date(new Date().setMonth(new Date().getMonth() + 1))
            .toISOString()
            .split("T")[0]
      );

      const currentInvoiceItems = [...formik.values.invoiceItems];
      const courseFeeIndex = currentInvoiceItems.findIndex(
        (item) =>
          item.item !== "Registration Fee" && item.item !== "Deposit Fee"
      );

      if (courseFeeIndex !== -1) {
        currentInvoiceItems[courseFeeIndex] = {
          item: data.courseFeeName || "Course Fee",
          itemAmount: "0",
          taxType: data.courseTaxId ? data.courseTaxId.toString() : "",
          gstAmount: "0",
          totalAmount: (data.courseFee || 0).toString(),
        };

        if (
          currentInvoiceItems[courseFeeIndex].taxType &&
          parseFloat(currentInvoiceItems[courseFeeIndex].totalAmount)
        ) {
          const tax = taxData.find(
            (t) =>
              t.id === parseInt(currentInvoiceItems[courseFeeIndex].taxType)
          );
          const totalAmount = parseFloat(
            currentInvoiceItems[courseFeeIndex].totalAmount || 0
          );
          if (tax && totalAmount !== 0) {
            const itemAmount = Math.round(totalAmount / (1 + tax.rate / 100));
            const gstAmount = Math.round(totalAmount - itemAmount);
            currentInvoiceItems[courseFeeIndex].itemAmount =
              itemAmount.toString();
            currentInvoiceItems[courseFeeIndex].gstAmount =
              gstAmount.toString();
          } else {
            currentInvoiceItems[courseFeeIndex].itemAmount =
              totalAmount.toString();
            currentInvoiceItems[courseFeeIndex].gstAmount = "0";
          }
        } else {
          currentInvoiceItems[courseFeeIndex].itemAmount =
            currentInvoiceItems[courseFeeIndex].totalAmount || "0";
          currentInvoiceItems[courseFeeIndex].gstAmount = "0";
        }

        formik.setFieldValue("invoiceItems", currentInvoiceItems);
      }

      const totalGST = currentInvoiceItems.reduce(
        (sum, item) => sum + parseFloat(item.gstAmount || 0),
        0
      );
      const totalAmountSum = currentInvoiceItems.reduce(
        (sum, item) => sum + parseFloat(item.totalAmount || 0),
        0
      );
      formik.setFieldValue("gst", totalGST.toString());
      formik.setFieldValue(
        "totalAmount",
        (
          totalAmountSum - parseFloat(formik.values.creditAdviceOffset || 0)
        ).toString()
      );

      setSchedulesData([data.classCode]);
    } catch (error) {
      toast.error("Error fetching invoice info");
      console.error(error);

      formik.setFieldValue("noOfLessons", selectedCourse.noOfLessons || "");
      formik.setFieldValue(
        "invoicePeriodFrom",
        selectedCourse.invoicePeriodFrom || ""
      );
      formik.setFieldValue(
        "invoicePeriodTo",
        selectedCourse.invoicePeriodTo || ""
      );
      formik.setFieldValue("schedule", selectedCourse.classCode || "");
      formik.setFieldValue(
        "dueDate",
        selectedCourse.invoicePeriodTo ||
          new Date(new Date().setMonth(new Date().getMonth() + 1))
            .toISOString()
            .split("T")[0]
      );

      const currentInvoiceItems = [...formik.values.invoiceItems];
      const courseFeeIndex = currentInvoiceItems.findIndex(
        (item) =>
          item.item !== "Registration Fee" && item.item !== "Deposit Fee"
      );

      if (courseFeeIndex !== -1) {
        currentInvoiceItems[courseFeeIndex] = {
          item: selectedCourse.courseFeeName || "Course Fee",
          itemAmount: "0",
          taxType: selectedCourse.courseTaxId
            ? selectedCourse.courseTaxId.toString()
            : "",
          gstAmount: "0",
          totalAmount: (selectedCourse.courseFee || 0).toString(),
        };

        if (
          currentInvoiceItems[courseFeeIndex].taxType &&
          parseFloat(currentInvoiceItems[courseFeeIndex].totalAmount)
        ) {
          const tax = taxData.find(
            (t) =>
              t.id === parseInt(currentInvoiceItems[courseFeeIndex].taxType)
          );
          const totalAmount = parseFloat(
            currentInvoiceItems[courseFeeIndex].totalAmount || 0
          );
          if (tax && totalAmount !== 0) {
            const itemAmount = Math.round(totalAmount / (1 + tax.rate / 100));
            const gstAmount = Math.round(totalAmount - itemAmount);
            currentInvoiceItems[courseFeeIndex].itemAmount =
              itemAmount.toString();
            currentInvoiceItems[courseFeeIndex].gstAmount =
              gstAmount.toString();
          } else {
            currentInvoiceItems[courseFeeIndex].itemAmount =
              totalAmount.toString();
            currentInvoiceItems[courseFeeIndex].gstAmount = "0";
          }
        } else {
          currentInvoiceItems[courseFeeIndex].itemAmount =
            currentInvoiceItems[courseFeeIndex].totalAmount || "0";
          currentInvoiceItems[courseFeeIndex].gstAmount = "0";
        }

        formik.setFieldValue("invoiceItems", currentInvoiceItems);
      }

      const totalGST = currentInvoiceItems.reduce(
        (sum, item) => sum + parseFloat(item.gstAmount || 0),
        0
      );
      const totalAmountSum = currentInvoiceItems.reduce(
        (sum, item) => sum + parseFloat(item.totalAmount || 0),
        0
      );
      formik.setFieldValue("gst", totalGST.toString());
      formik.setFieldValue(
        "totalAmount",
        (
          totalAmountSum - parseFloat(formik.values.creditAdviceOffset || 0)
        ).toString()
      );

      setSchedulesData(
        studentOverallData.courses
          .filter((course) => course.courseId === courseId)
          .map((course) => course.classCode)
          .filter((code, index, self) => self.indexOf(code) === index)
      );
    }
  };

  // Handle package change
  const handlePackageChange = (packageId) => {
    if (!packageId) return;

    const selectedCourse = studentOverallData.courses?.find(
      (course) => course.packageId === packageId
    );
    const selectedPackage = centerPackageOptions.find(
      (pkg) => pkg.value === packageId
    );

    if (selectedCourse) {
      formik.setFieldValue("noOfLessons", selectedCourse.noOfLessons || "");
      formik.setFieldValue(
        "invoicePeriodFrom",
        selectedCourse.invoicePeriodFrom || ""
      );
      formik.setFieldValue(
        "invoicePeriodTo",
        selectedCourse.invoicePeriodTo || ""
      );
      formik.setFieldValue("course", selectedCourse.courseId || "");
      formik.setFieldValue("schedule", selectedCourse.classCode || "");
      formik.setFieldValue(
        "dueDate",
        selectedCourse.invoicePeriodTo ||
          new Date(new Date().setMonth(new Date().getMonth() + 1))
            .toISOString()
            .split("T")[0]
      );

      const currentInvoiceItems = [...formik.values.invoiceItems];
      const courseFeeIndex = currentInvoiceItems.findIndex(
        (item) =>
          item.item !== "Registration Fee" && item.item !== "Deposit Fee"
      );

      if (courseFeeIndex !== -1) {
        currentInvoiceItems[courseFeeIndex] = {
          item: selectedCourse.courseFeeName || "Course Fee",
          itemAmount: "0",
          taxType: selectedCourse.courseTaxId
            ? selectedCourse.courseTaxId.toString()
            : "",
          gstAmount: "0",
          totalAmount: (selectedCourse.courseFee || 0).toString(),
        };

        if (
          currentInvoiceItems[courseFeeIndex].taxType &&
          parseFloat(currentInvoiceItems[courseFeeIndex].totalAmount)
        ) {
          const tax = taxData.find(
            (t) =>
              t.id === parseInt(currentInvoiceItems[courseFeeIndex].taxType)
          );
          const totalAmount = parseFloat(
            currentInvoiceItems[courseFeeIndex].totalAmount || 0
          );
          if (tax && totalAmount !== 0) {
            const itemAmount = Math.round(totalAmount / (1 + tax.rate / 100));
            const gstAmount = Math.round(totalAmount - itemAmount);
            currentInvoiceItems[courseFeeIndex].itemAmount =
              itemAmount.toString();
            currentInvoiceItems[courseFeeIndex].gstAmount =
              gstAmount.toString();
          } else {
            currentInvoiceItems[courseFeeIndex].itemAmount =
              totalAmount.toString();
            currentInvoiceItems[courseFeeIndex].gstAmount = "0";
          }
        } else {
          currentInvoiceItems[courseFeeIndex].itemAmount =
            currentInvoiceItems[courseFeeIndex].totalAmount || "0";
          currentInvoiceItems[courseFeeIndex].gstAmount = "0";
        }

        formik.setFieldValue("invoiceItems", currentInvoiceItems);
      }

      const totalGST = currentInvoiceItems.reduce(
        (sum, item) => sum + parseFloat(item.gstAmount || 0),
        0
      );
      const totalAmountSum = currentInvoiceItems.reduce(
        (sum, item) => sum + parseFloat(item.totalAmount || 0),
        0
      );
      formik.setFieldValue("gst", totalGST.toString());
      formik.setFieldValue(
        "totalAmount",
        (
          totalAmountSum - parseFloat(formik.values.creditAdviceOffset || 0)
        ).toString()
      );
    } else if (selectedPackage) {
      formik.setFieldValue("noOfLessons", selectedPackage.noOfLessons || "");
    }
  };

  // Handle tax type change
  const handleSelectChange = (index, taxId) => {
    formik.setFieldValue(`invoiceItems[${index}].taxType`, taxId);
    const totalAmount = parseFloat(
      formik.values.invoiceItems[index].totalAmount || 0
    );

    if (taxId && totalAmount !== 0) {
      const tax = taxData.find((t) => t.id === parseInt(taxId));
      let itemAmount = totalAmount;
      let gstAmount = 0;
      if (tax) {
        itemAmount = Math.round(totalAmount / (1 + tax.rate / 100));
        gstAmount = Math.round(totalAmount - itemAmount);
      }
      formik.setFieldValue(
        `invoiceItems[${index}].itemAmount`,
        itemAmount.toString()
      );
      formik.setFieldValue(
        `invoiceItems[${index}].gstAmount`,
        gstAmount.toString()
      );
    } else {
      formik.setFieldValue(
        `invoiceItems[${index}].itemAmount`,
        totalAmount.toString()
      );
      formik.setFieldValue(`invoiceItems[${index}].gstAmount`, "0");
    }

    const totalGST = formik.values.invoiceItems.reduce(
      (sum, item) => sum + parseFloat(item.gstAmount || 0),
      0
    );
    const totalAmountSum = formik.values.invoiceItems.reduce(
      (sum, item) => sum + parseFloat(item.totalAmount || 0),
      0
    );
    formik.setFieldValue("gst", totalGST.toString());
    formik.setFieldValue(
      "totalAmount",
      (
        totalAmountSum - parseFloat(formik.values.creditAdviceOffset || 0)
      ).toString()
    );
  };

  // Handle total amount change
  const handleTotalAmountChange = (index, value) => {
    const totalAmount = parseFloat(value || 0);
    formik.setFieldValue(
      `invoiceItems[${index}].totalAmount`,
      totalAmount.toString()
    );
    const taxId = formik.values.invoiceItems[index].taxType;

    if (taxId && totalAmount !== 0) {
      const tax = taxData.find((t) => t.id === parseInt(taxId));
      let itemAmount = totalAmount;
      let gstAmount = 0;
      if (tax) {
        itemAmount = Math.round(totalAmount / (1 + tax.rate / 100));
        gstAmount = Math.round(totalAmount - itemAmount);
      }
      formik.setFieldValue(
        `invoiceItems[${index}].itemAmount`,
        itemAmount.toString()
      );
      formik.setFieldValue(
        `invoiceItems[${index}].gstAmount`,
        gstAmount.toString()
      );
    } else {
      formik.setFieldValue(
        `invoiceItems[${index}].itemAmount`,
        totalAmount.toString()
      );
      formik.setFieldValue(`invoiceItems[${index}].gstAmount`, "0");
    }

    const totalGST = formik.values.invoiceItems.reduce(
      (sum, item) => sum + parseFloat(item.gstAmount || 0),
      0
    );
    const totalAmountSum = formik.values.invoiceItems.reduce(
      (sum, item) => sum + parseFloat(item.totalAmount || 0),
      0
    );
    formik.setFieldValue("gst", totalGST.toString());
    formik.setFieldValue(
      "totalAmount",
      (
        totalAmountSum - parseFloat(formik.values.creditAdviceOffset || 0)
      ).toString()
    );
  };

  // Handle row deletion
  const handleRowDelete = (index) => {
    const courseFeeName =
      studentOverallData.courses?.[0]?.courseFeeName || "Course Fee";
    if (
      formik.values.invoiceItems[index]?.item !== "Registration Fee" &&
      formik.values.invoiceItems[index]?.item !== courseFeeName &&
      formik.values.invoiceItems[index]?.item !== "Deposit Fee"
    ) {
      const newInvoiceItems = formik.values.invoiceItems.filter(
        (_, i) => i !== index
      );
      formik.setFieldValue("invoiceItems", newInvoiceItems);

      const totalGST = newInvoiceItems.reduce(
        (sum, item) => sum + parseFloat(item.gstAmount || 0),
        0
      );
      const totalAmount = newInvoiceItems.reduce(
        (sum, item) => sum + parseFloat(item.totalAmount || 0),
        0
      );
      formik.setFieldValue("gst", totalGST.toString());
      formik.setFieldValue(
        "totalAmount",
        (
          totalAmount - parseFloat(formik.values.creditAdviceOffset || 0)
        ).toString()
      );
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchTaxData();
    fetchStudent();
    fetchPackagesByCenter();
  }, [selectedCenter]);

  // Handle studentID from search params
  useEffect(() => {
    if (studentID) {
      handleStudentChange(studentID);
    }
  }, [studentID]);

  // Scroll to first error on submit
  useEffect(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      const errorField = Object.keys(formik.errors)[0];
      const errorElement =
        document.querySelector(`[name="${errorField}"]`) ||
        document.querySelector(`[name="invoiceItems[0].${errorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        errorElement.focus();
      }
    }
  }, [formik.submitCount, formik.errors]);

  return (
    <div className="container-fluid">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Invoice and Payment
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          <Link to="/invoice" className="custom-breadcrumb">
            Invoice
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="active breadcrumb-item" aria-current="page">
          Invoice Add
        </li>
      </ol>
      <form
        onSubmit={formik.handleSubmit}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !formik.isSubmitting) {
            e.preventDefault();
          }
        }}
      >
        <div className="card">
          <div
            className="d-flex align-items-center justify-content-between p-1 mb-4 px-4"
            style={{ background: "#f5f7f9" }}
          >
            <div className="d-flex align-items-center">
              <div className="d-flex">
                <div className="active dot"></div>
              </div>
              <span className="text-muted me-2">Add Invoice</span>
            </div>
            <div className="d-flex align-items-center my-2">
              <Link to="/invoice">
                <button type="button" className="btn btn-border btn-sm mx-2">
                  Back
                </button>
              </Link>
              <button
                type="submit"
                className="btn btn-button btn-sm"
                disabled={loadIndicator}
              >
                {loadIndicator && (
                  <span
                    className="me-2 spinner-border spinner-border-sm"
                    aria-hidden="true"
                  ></span>
                )}
                Generate
              </button>
            </div>
          </div>
          <div className="container-fluid py-3">
            <div className="row mt-3">
              <div className="col-12 col-lg-6 col-md-6 px-5">
                <div className="text-start mt-3">
                  <label htmlFor="student" className="fw-medium mb-1">
                    Student<span className="text-danger">*</span>
                  </label>
                  <br />
                  <Select
                    options={studentOptions}
                    name="student"
                    value={studentOptions.find(
                      (option) => option.value === formik.values.student
                    )}
                    onChange={(selectedOption) => {
                      const studentId = selectedOption
                        ? selectedOption.value
                        : "";
                      formik.setFieldValue("student", studentId);
                      if (studentId) {
                        handleStudentChange(studentId);
                      } else {
                        resetFormFields();
                      }
                    }}
                    placeholder="Select Student"
                    isSearchable
                    isClearable
                    className={`${
                      formik.touched.student && formik.errors.student
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.student && formik.errors.student && (
                    <div className="invalid-feedback">
                      {formik.errors.student}
                    </div>
                  )}
                </div>
                <div className="text-start mt-3">
                  <label htmlFor="parent" className="fw-medium mb-1">
                    Parent<span className="text-danger">*</span>
                  </label>
                  <br />
                  <input
                    {...formik.getFieldProps("parent")}
                    className={`form-control ${
                      formik.touched.parent && formik.errors.parent
                        ? "is-invalid"
                        : ""
                    }`}
                    type="text"
                    readOnly
                  />
                  {formik.touched.parent && formik.errors.parent && (
                    <div className="invalid-feedback">
                      {formik.errors.parent}
                    </div>
                  )}
                </div>
                <div className="text-start mt-3">
                  <label htmlFor="course" className="fw-medium mb-1">
                    Course<span className="text-danger">*</span>
                  </label>
                  <br />
                  <Select
                    options={courseOptions}
                    name="course"
                    value={courseOptions.find(
                      (option) => option.value === formik.values.course
                    )}
                    onChange={(selectedOption) => {
                      const courseId = selectedOption
                        ? selectedOption.value
                        : "";
                      formik.setFieldValue("course", courseId);
                      if (courseId) {
                        handleCourseChange(courseId);
                      } else {
                        resetFormFields();
                      }
                    }}
                    placeholder="Select Course"
                    isSearchable
                    isClearable
                    className={`${
                      formik.touched.course && formik.errors.course
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.course && formik.errors.course && (
                    <div className="invalid-feedback">
                      {formik.errors.course}
                    </div>
                  )}
                </div>
                <div className="text-start mt-3">
                  <label htmlFor="schedule" className="fw-medium mb-1">
                    Schedule
                  </label>
                  <br />
                  <select
                    {...formik.getFieldProps("schedule")}
                    className={`form-select ${
                      formik.touched.schedule && formik.errors.schedule
                        ? "is-invalid"
                        : ""
                    }`}
                  >
                    <option value="">Select a schedule</option>
                    {schedulesData.map((schedule, index) => (
                      <option key={index} value={schedule}>
                        {schedule}
                      </option>
                    ))}
                  </select>
                  {formik.touched.schedule && formik.errors.schedule && (
                    <div className="invalid-feedback">
                      {formik.errors.schedule}
                    </div>
                  )}
                </div>
                <div className="text-start mt-3">
                  <label htmlFor="packageId" className="fw-medium mb-1">
                    Package<span className="text-danger">*</span>
                  </label>
                  <br />
                  <Select
                    options={packageOptions}
                    name="packageId"
                    value={packageOptions.find(
                      (option) => option.value === formik.values.packageId
                    )}
                    onChange={(selectedOption) => {
                      const packageId = selectedOption
                        ? selectedOption.value
                        : "";
                      formik.setFieldValue("packageId", packageId);
                      if (packageId) {
                        handlePackageChange(packageId);
                      }
                    }}
                    placeholder="Select Package"
                    isSearchable
                    isClearable
                    className={`${
                      formik.touched.packageId && formik.errors.packageId
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.packageId && formik.errors.packageId && (
                    <div className="invalid-feedback">
                      {formik.errors.packageId}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-6 col-md-6 px-5">
                <div className="text-start mt-3">
                  <label htmlFor="invoiceDate" className="fw-medium mb-1">
                    Invoice Date<span className="text-danger">*</span>
                  </label>
                  <br />
                  <input
                    {...formik.getFieldProps("invoiceDate")}
                    className={`form-control ${
                      formik.touched.invoiceDate && formik.errors.invoiceDate
                        ? "is-invalid"
                        : ""
                    }`}
                    type="date"
                    onChange={(e) => {
                      const invoiceDate = e.target.value;
                      formik.setFieldValue("invoiceDate", invoiceDate);
                      if (!invoiceDate) {
                        formik.setFieldValue("dueDate", "");
                      } else {
                        const newDueDate =
                          formik.values.invoicePeriodTo ||
                          new Date(
                            new Date(invoiceDate).setMonth(
                              new Date(invoiceDate).getMonth() + 1
                            )
                          )
                            .toISOString()
                            .split("T")[0];
                        formik.setFieldValue("dueDate", newDueDate);
                      }
                    }}
                  />
                  {formik.touched.invoiceDate && formik.errors.invoiceDate && (
                    <div className="invalid-feedback">
                      {formik.errors.invoiceDate}
                    </div>
                  )}
                </div>
                <div className="text-start mt-3">
                  <label htmlFor="dueDate" className="fw-medium mb-1">
                    Due Date<span className="text-danger">*</span>
                  </label>
                  <br />
                  <input
                    {...formik.getFieldProps("dueDate")}
                    className={`form-control ${
                      formik.touched.dueDate && formik.errors.dueDate
                        ? "is-invalid"
                        : ""
                    }`}
                    type="date"
                  />
                  {formik.touched.dueDate && formik.errors.dueDate && (
                    <div className="invalid-feedback">
                      {formik.errors.dueDate}
                    </div>
                  )}
                </div>
                <div className="text-start mt-3">
                  <label htmlFor="invoicePeriodFrom" className="fw-medium mb-1">
                    Invoice Period From<span className="text-danger">*</span>
                  </label>
                  <br />
                  <input
                    {...formik.getFieldProps("invoicePeriodFrom")}
                    className={`form-control ${
                      formik.touched.invoicePeriodFrom &&
                      formik.errors.invoicePeriodFrom
                        ? "is-invalid"
                        : ""
                    }`}
                    type="date"
                    readOnly
                  />
                  {formik.touched.invoicePeriodFrom &&
                    formik.errors.invoicePeriodFrom && (
                      <div className="invalid-feedback">
                        {formik.errors.invoicePeriodFrom}
                      </div>
                    )}
                </div>
                <div className="text-start mt-3">
                  <label htmlFor="invoicePeriodTo" className="fw-medium mb-1">
                    Invoice Period To<span className="text-danger">*</span>
                  </label>
                  <br />
                  <input
                    {...formik.getFieldProps("invoicePeriodTo")}
                    className={`form-control ${
                      formik.touched.invoicePeriodTo &&
                      formik.errors.invoicePeriodTo
                        ? "is-invalid"
                        : ""
                    }`}
                    type="date"
                    readOnly
                    onChange={(e) => {
                      formik.setFieldValue("invoicePeriodTo", e.target.value);
                      if (e.target.value) {
                        formik.setFieldValue("dueDate", e.target.value);
                      }
                    }}
                  />
                  {formik.touched.invoicePeriodTo &&
                    formik.errors.invoicePeriodTo && (
                      <div className="invalid-feedback">
                        {formik.errors.invoicePeriodTo}
                      </div>
                    )}
                </div>
                <div className="text-start mt-3">
                  <label htmlFor="noOfLessons" className="fw-medium mb-1">
                    Number of Lessons
                  </label>
                  <br />
                  <input
                    {...formik.getFieldProps("noOfLessons")}
                    className="form-control"
                    type="text"
                    readOnly
                  />
                </div>
              </div>
              <div className="col-12 px-5 mt-3">
                <label htmlFor="remark" className="fw-medium mb-1">
                  Remarks
                </label>
                <br />
                <textarea
                  {...formik.getFieldProps("remark")}
                  className="form-control"
                  type="text"
                  placeholder="Remarks"
                  style={{ height: "7rem" }}
                  maxLength={200}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.stopPropagation();
                    }
                  }}
                />
              </div>
            </div>
            <div className="flex-nowrap row mt-5 pt-5">
              <div className="col-12">
                <div className="table-responsive">
                  <table
                    className="table table-light table-bordered table-nowrap"
                    style={{ borderCollapse: "collapse" }}
                  >
                    <thead style={{ backgroundColor: "#f5f5f5" }}>
                      <tr>
                        <th
                          style={{
                            width: "27%",
                            padding: "10px",
                            fontWeight: "bold",
                          }}
                        >
                          Item<span className="text-danger">*</span>
                        </th>
                        <th
                          style={{
                            width: "12%",
                            padding: "10px",
                            fontWeight: "bold",
                            textAlign: "left",
                          }}
                        >
                          Item Amount (Exc GST)
                          <span className="text-danger">*</span>
                        </th>
                        <th
                          style={{
                            width: "20%",
                            padding: "10px",
                            fontWeight: "bold",
                            textAlign: "left",
                          }}
                        >
                          Tax Type<span className="text-danger">*</span>
                        </th>
                        <th
                          style={{
                            width: "8%",
                            padding: "10px",
                            fontWeight: "bold",
                            textAlign: "left",
                          }}
                        >
                          GST Amount<span className="text-danger">*</span>
                        </th>
                        <th
                          style={{
                            width: "10%",
                            padding: "10px",
                            fontWeight: "bold",
                            textAlign: "left",
                          }}
                        >
                          Total Amount (Inc GST)
                          <span className="text-danger">*</span>
                        </th>
                        <th style={{ width: "5%", padding: "10px" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formik.values.invoiceItems.map((item, index) => {
                        const courseFeeName =
                          studentOverallData.courses?.[0]?.courseFeeName ||
                          "Course Fee";
                        const isNonDeletable =
                          item.item === "Registration Fee" ||
                          item.item === courseFeeName ||
                          item.item === "Deposit Fee";

                        return (
                          <tr key={index}>
                            <td style={{ padding: "8px" }}>
                              <textarea
                                rows={2}
                                {...formik.getFieldProps(
                                  `invoiceItems[${index}].item`
                                )}
                                className={`form-control ${
                                  formik.touched.invoiceItems?.[index]?.item &&
                                  formik.errors.invoiceItems?.[index]?.item
                                    ? "is-invalid"
                                    : ""
                                }`}
                                type="text"
                                style={{ width: "100%", padding: "6px" }}
                              />
                              {formik.touched.invoiceItems?.[index]?.item &&
                                formik.errors.invoiceItems?.[index]?.item && (
                                  <div className="invalid-feedback">
                                    {formik.errors.invoiceItems[index].item}
                                  </div>
                                )}
                            </td>
                            <td style={{ padding: "8px", textAlign: "right" }}>
                              <input
                                {...formik.getFieldProps(
                                  `invoiceItems[${index}].itemAmount`
                                )}
                                className={`form-control ${
                                  formik.touched.invoiceItems?.[index]
                                    ?.itemAmount &&
                                  formik.errors.invoiceItems?.[index]
                                    ?.itemAmount
                                    ? "is-invalid"
                                    : ""
                                }`}
                                type="number"
                                style={{ padding: "6px", textAlign: "right" }}
                                readOnly
                              />
                              {formik.touched.invoiceItems?.[index]
                                ?.itemAmount &&
                                formik.errors.invoiceItems?.[index]
                                  ?.itemAmount && (
                                  <div className="invalid-feedback">
                                    {
                                      formik.errors.invoiceItems[index]
                                        .itemAmount
                                    }
                                  </div>
                                )}
                            </td>
                            <td style={{ padding: "8px" }}>
                              <select
                                className={`form-select ${
                                  formik.touched.invoiceItems?.[index]
                                    ?.taxType &&
                                  formik.errors.invoiceItems?.[index]?.taxType
                                    ? "is-invalid"
                                    : ""
                                }`}
                                {...formik.getFieldProps(
                                  `invoiceItems[${index}].taxType`
                                )}
                                style={{ padding: "6px" }}
                                onChange={(e) =>
                                  handleSelectChange(index, e.target.value)
                                }
                              >
                                <option value="">Select Tax Type</option>
                                {taxData &&
                                  taxData.map((tax) => (
                                    <option key={tax.id} value={tax.id}>
                                      {`${tax.taxType} ${tax.rate}%`}
                                    </option>
                                  ))}
                              </select>
                              {formik.touched.invoiceItems?.[index]?.taxType &&
                                formik.errors.invoiceItems?.[index]
                                  ?.taxType && (
                                  <div className="invalid-feedback">
                                    {formik.errors.invoiceItems[index].taxType}
                                  </div>
                                )}
                            </td>
                            <td style={{ padding: "8px", textAlign: "right" }}>
                              <input
                                {...formik.getFieldProps(
                                  `invoiceItems[${index}].gstAmount`
                                )}
                                className={`form-control ${
                                  formik.touched.invoiceItems?.[index]
                                    ?.gstAmount &&
                                  formik.errors.invoiceItems?.[index]?.gstAmount
                                    ? "is-invalid"
                                    : ""
                                }`}
                                type="number"
                                style={{
                                  width: "100%",
                                  padding: "6px",
                                  textAlign: "right",
                                }}
                                readOnly
                              />
                              {formik.touched.invoiceItems?.[index]
                                ?.gstAmount &&
                                formik.errors.invoiceItems?.[index]
                                  ?.gstAmount && (
                                  <div className="invalid-feedback">
                                    {
                                      formik.errors.invoiceItems[index]
                                        .gstAmount
                                    }
                                  </div>
                                )}
                            </td>
                            <td style={{ padding: "8px", textAlign: "right" }}>
                              <input
                                name={`invoiceItems[${index}].totalAmount`}
                                value={
                                  formik.values.invoiceItems[index]
                                    ?.totalAmount || ""
                                }
                                onChange={(e) =>
                                  handleTotalAmountChange(index, e.target.value)
                                }
                                onBlur={formik.handleBlur}
                                className={`form-control ${
                                  formik.touched.invoiceItems?.[index]
                                    ?.totalAmount &&
                                  formik.errors.invoiceItems?.[index]
                                    ?.totalAmount
                                    ? "is-invalid"
                                    : ""
                                }`}
                                type="number"
                                style={{
                                  width: "100%",
                                  padding: "6px",
                                  textAlign: "right",
                                }}
                              />
                              {formik.touched.invoiceItems?.[index]
                                ?.totalAmount &&
                                formik.errors.invoiceItems?.[index]
                                  ?.totalAmount && (
                                  <div className="invalid-feedback">
                                    {
                                      formik.errors.invoiceItems[index]
                                        .totalAmount
                                    }
                                  </div>
                                )}
                            </td>
                            <td style={{ padding: "8px", textAlign: "center" }}>
                              {!isNonDeletable && (
                                <button
                                  type="button"
                                  className="btn btn-white border-white"
                                  onClick={() => handleRowDelete(index)}
                                >
                                  <ImCancelCircle className="text-danger fs-6 fw-medium" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-12 text-end mt-3">
                <button
                  type="button"
                  className="btn btn-sm text-white"
                  style={{
                    fontWeight: "600px !important",
                    background: "#eb862a",
                  }}
                  onClick={() => {
                    formik.setFieldValue("invoiceItems", [
                      ...formik.values.invoiceItems,
                      {
                        item: "",
                        itemAmount: "",
                        taxType: "",
                        gstAmount: "",
                        totalAmount: "",
                      },
                    ]);
                  }}
                >
                  Add Row
                </button>
              </div>
              <div className="col-12 col-lg-6 col-md-6"></div>
              <div className="col-12 col-lg-6 col-md-6">
                <div className="">
                  <div className="text-start mt-3">
                    <label
                      htmlFor="creditAdviceOffset"
                      className="fw-medium mb-1"
                    >
                      Credit Advice Offset
                    </label>
                    <br />
                    <input
                      {...formik.getFieldProps("creditAdviceOffset")}
                      className="form-control"
                      type="text"
                      readOnly
                    />
                  </div>
                  <div className="text-start mt-3">
                    <label htmlFor="gst" className="fw-medium mb-1">
                      GST Amount
                    </label>
                    <br />
                    <input
                      {...formik.getFieldProps("gst")}
                      className="form-control"
                      type="text"
                      readOnly
                    />
                  </div>
                  <div className="text-start mt-3">
                    <label htmlFor="totalAmount" className="fw-medium mb-1">
                      Total Amount
                    </label>
                    <br />
                    <input
                      {...formik.getFieldProps("totalAmount")}
                      className="form-control"
                      type="text"
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default InvoiceAdd;
